import React, { useEffect, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { ScrollText, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_LABELS = {
  'tenant.create': 'Mandant angelegt',
  'tenant.update': 'Mandant bearbeitet',
  'tenant.status': 'Mandant-Status geändert',
  'tenant.delete': 'Mandant gelöscht',
  'medidata.update': 'MediData-Konfiguration geändert',
  '2fa.setup_started': '2FA-Einrichtung gestartet',
  '2fa.activated': '2FA aktiviert',
  '2fa.disabled': '2FA deaktiviert',
  'login.totp_required': 'Login: 2FA angefordert',
  'login.totp_success': 'Login: 2FA bestätigt',
  'login.totp_failed': 'Login: 2FA fehlgeschlagen',
  'support.login_start': 'Support-Login gestartet',
  'support.login_end': 'Support-Login beendet',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-CH', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function AuditPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await superAdminApi.audit(200);
      setEntries(data.entries || data || []);
    } catch (e) {
      toast.error('Audit-Log konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="sa-audit" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">Audit-Log</h1>
          <p className="mt-1 text-sm text-slate-600">Kritische Super-Admin-Aktionen, chronologisch.</p>
        </div>
        <button
          onClick={load}
          data-testid="sa-audit-refresh"
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
        >
          <RefreshCw className="h-4 w-4" /> Aktualisieren
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Lädt…</div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 py-16 text-slate-500">
          <ScrollText className="h-8 w-8" />
          <span className="text-sm">Noch keine Einträge.</span>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-white/80 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Zeit</th>
                <th className="px-4 py-3 font-medium">Aktion</th>
                <th className="px-4 py-3 font-medium">Akteur</th>
                <th className="px-4 py-3 font-medium">Ziel</th>
                <th className="px-4 py-3 font-medium">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {entries.map((e, i) => (
                <tr key={e.id || i} className="bg-slate-50 hover:bg-slate-100" data-testid="sa-audit-row">
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">{fmt(e.at || e.created_at)}</td>
                  <td className="px-4 py-3 text-slate-800">{ACTION_LABELS[e.action] || e.action}</td>
                  <td className="px-4 py-3 text-slate-600">{e.actor_email || e.actor_id || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {e.target_type ? `${e.target_type}${e.target_id ? ' · ' + String(e.target_id).slice(0, 8) : ''}` : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{e.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
